import { useEffect, useMemo, useRef, useState, useCallback, type CSSProperties } from 'react'
import { useCreditsGuard } from '../../../hooks/useCreditsGuard'
import { hasFreeLeft, useModelPricing } from '../../../hooks/useModelPricing'
import CreditsShortNote from '../../../components/CreditsShortNote'
import { useLocation, useNavigate } from 'react-router-dom'

import { useGenerate } from '../../../context/GenerateContext'
import { useVariant } from '../../../context/VariantContext'
import { useGlobalGeneration } from '../../../context/GlobalGenerationContext'
import { useAuth } from '../../../context/AuthContext'
import { GENERATE_TOOL_COMPACT_KEY } from '../utils/constants'
import { detachStyleCard, getAttachedStyleCard, setAttachedCustomValue, useAttachedStyleCard } from '../../../api/attachedStyleCard'
import { buildVariantPrompt, substitutePhotoSlots, usedPhotoSlotNums, variantFieldValue } from '../../../utils/variantPrompt'
import NumberStepper from '../../../components/NumberStepper'
import CardMediaBar from '../../../components/CardMediaBar'
import { useAuthLink } from '../../../hooks/useAuthModal'
import BeforeAfterSlider from '../../../components/BeforeAfterSlider'
import Logo from '../../../icons/Logo'
import { Icon } from '../../../../public/icons/Icon.tsx'
import DotFieldLoader from '../../../components/DotFieldLoader'
import PencilIcon from '../../../icons/PencilIcon'
import CardImageLightbox from '../../../components/CardImageLightbox'
import { type CardReferenceMeta, cardReferenceMeta, cardReferenceUrl } from '../../../api/cardReference'
import { durationFollowsReference, durationLock, isSeedance25Model, trendVideoFollows } from '../utils/referenceLimits'
import ToolReferenceThumbs, { type ToolReferenceItem } from './ToolReferenceThumbs'
import { soundPref } from '../../../utils/cardSound'
import { announceTileSound, claimTileSound, onTileSoundClaimed, releaseTileSound, tileSoundToken } from '../../../utils/tileSound'
import MediaPlayer from '../../../components/MediaPlayer'

import SettingsToolsPill from './SettingsToolsPill'
import CreateButton from './CreateButton'
import DetachButton from './DetachButton'
import WidgetSectionNav from './WidgetSectionNav'
import WidgetModelPicker from './WidgetModelPicker'
import UploadsRow from './UploadsRow'
import UploadThumb from './UploadThumb'
import Tooltip from '../../../components/Tooltip'
import CardModal from '../../../components/CardModal'
import { useUploadPickerMenu } from '../hooks/useUploadPickerMenu'
import { usePipelineFlow } from '../hooks/usePipelineFlow'
import { uploadAndGetUrl, type UploadedFile } from '../utils/uploadHelpers'
import { photoSlotCapacity } from '../../../photo-slots'
import { prepareImageInput } from '../utils/imagePrepare'
import { UPLOAD_SIZE_MB } from '../hooks/useGenerateUploads'
import ScenePickerHost from './ScenePicker'
import CharacterRow from './CharacterRow'
import {
	clearScene,
	getScene,
	getStickyScene,
	SCENE_REMOVE_EVENT,
	setScene,
	setStickyScene,
} from '../../../api/sceneSelection'
import { assetThumb, parseAssetError, planAssets } from '../../../api/assets'
import { UNFIT_TEXTS } from '../../../utils/characterFit'
import { pathWithoutTool } from '../../bots/utils/cardSeo.ts'
import { showToast } from '../../../components/Toast'
import { RETRY_NOTE, RETRY_TITLE, taskTick } from '../../../api/taskTicks'
import { useTaskTicks } from '../../../hooks/useTaskTicks'
import { loadPendingSnapshot } from '../../../context/historyPersistence'
import { useResultTrack } from '../hooks/useResultTrack'
import { isMotionCard } from '../motion/motionTool'
import { useMotionMode } from '../motion/useMotionMode'
import MotionClipZone from '../motion/MotionClipZone'
import MotionWhoZone from '../motion/MotionWhoZone'
import ClearIcon from './ClearIcon'
import { hasFrameRefModes } from '../../../utils/frameRefModes'

// Уже этого ряд зон «пример · поля · результат» не держит поля в ряд: пример 225px + поля + результат ≥170px.
const ZONES_NARROW_PX = 760

// aspectRatio настройки → CSS aspect-ratio для рамки результата.
const AR_CSS: Record<string, string> = {
	'1:1': '1 / 1',
	'3:4': '3 / 4',
	'4:3': '4 / 3',
	'16:9': '16 / 9',
	'9:16': '9 / 16',
	'21:9': '21 / 9',
	'3:2': '3 / 2',
	'2:3': '2 / 3',
}

/**
 * Фокус-режим инструмента — БЕЗ собственной рамки: живёт внутри тела виджета
 * (.gw__input-col__body), под штатной шапкой ModelBadge. Это тот же виджет —
 * просто «середина» (поле промпта) заменена на компактные зоны инструмента:
 * пример-слайдер (≤ верхнего showcase), загрузка своего фото, результат в
 * выбранном формате + нижняя панель (стиль-селект + настройки модели +
 * «Создать»). Тема наследуется от виджета (light/dark, см. _tool-mode.scss).
 *
 * Селект модели скрыт (уходит вместе с GenerateInputBar). Выход из режима —
 * штатная «Очистить» бейджа: открепляет карточку → toolMode гаснет → бар.
 * Стиль под капотом патчит промпт через VariantContext (промпт-консьюмер бара
 * тут не смонтирован, поэтому переносим patch в gen.prompt эффектом ниже).
 */

// Слот фото пайплайна — ТОТ ЖЕ UploadThumb, что в 1-шаговом tool-режиме
// (единый вид: крупный dropzone с подписью, превью+Tooltip, статус, remove).
// Свой input/ref на слот — клик по любому пустому открывает выбор, файл
// дописывается в конец набора (handlePipePhotos). isNext — первый пустой слот
// (индикатор загрузки крутится на нём).
function PipeUploadSlot({ file, isNext, busy, uploading, label, onAdd, onRemove, characterMark }: {
	file: UploadedFile | null
	isNext: boolean
	busy: boolean
	uploading: boolean
	label: string
	onAdd: (e: React.ChangeEvent<HTMLInputElement>) => void
	onRemove: () => void
	characterMark?: { avatar: string | null; letter: string }
}) {
	const ref = useRef<HTMLInputElement | null>(null)
	return (
		<UploadThumb
			file={file}
			isUploading={uploading && isNext}
			isLoading={busy}
			accept="image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif"
			inputRef={ref}
			onChange={onAdd}
			onRemove={onRemove}
			icon={<Icon id="gen-add" size={20} />}
			label={label}
			characterMark={characterMark}
		/>
	)
}

/**
 * Пустой слот результата: спящая решётка + лого по центру + подпись у нижнего
 * края рамки.
 *
 * Решётка — ТОТ ЖЕ DotFieldLoader, что рисует генерацию, только в покое
 * (idle): та же геометрия, тот же брендовый градиент. Поэтому старт генерации
 * не подменяет плейсхолдер другим объектом — точки остаются на своих местах,
 * просто решётка уходит в фон, а поверх неё начинает бродить пятно. Рамку в
 * покое намеренно ничем не обводим: контур пришлось бы снимать при появлении
 * результата, и он окантовал бы кадр (разбор шести вариантов с владельцем,
 * 22.09: сплошная/пунктирная рамка отвергнуты, выбрана решётка).
 *
 * Подпись внизу, а не под лого: она — справка, а не заголовок, и у нижнего
 * края встаёт ровно туда же, где во время генерации живёт подпись фазы
 * (.gtm__result-t--over) — строка не прыгает при старте.
 */
function ResultEmpty({ text }: { text: string }) {
	return (
		<div className="gtm__result-empty gtm__result-empty--hint">
			<DotFieldLoader idle />
			{/* Знак лежит НА решётке, а не внутри неё: под ним мягкое «окно» в
			    поле (см. .gtm__result-mark::before) — иначе точки бегут сквозь
			    прозрачные части логотипа (правка владельца 22.09). */}
			<span className="gtm__result-mark">
				<Logo size={76} className="gtm__result-logo" />
			</span>
			<span className="gtm__result-t">{text}</span>
		</div>
	)
}

export default function GenerateToolMode({
	onOpenResult,
}: {
	/** Клик по готовой картинке-результату — хост открывает свой модал
	 *  результата (rg-modal). GTM-запись помечается promptLocked — модал
	 *  прячет промпт и модель (промпт карточки юзеру не показываем).
	 *  url — какой ролик открыть: у видео с версией по кнопке «Наложить трек»
	 *  выбранный в переключателе «Оригинал | Трек» (28.09). */
	onOpenResult?: (url?: string) => void
} = {}) {
	const gen = useGenerate()
	const navigate = useNavigate()
	const variant = useVariant()
	const globalGen = useGlobalGeneration()
	const card = useAttachedStyleCard()
	// Звук видео с треком (28.09, services/audio_layer на сервере): пока провайдер не пропускает звук и сервер
	// пересоздаёт ролик — подпись «Пересоздаём ролик» вместо ошибки; у готового ролика — «Наложить трек» и
	// переключатель «Оригинал | Трек».
	const genRunning = gen.isLoading || globalGen.status === 'running'
	useTaskTicks(genRunning)
	const retry = genRunning ? taskTick(loadPendingSnapshot()?.taskId) : null
	const track = useResultTrack(gen.isVideo && !genRunning ? gen.lastRequestId : null)
	const shownResult = track.showTrack && track.version ? track.version.result_url : gen.result
	// Гость: виджет мьютится стеклом с CTA входа (см. .gtm__guest) вместо
	// голой ошибки «Not authenticated» при попытке генерации. Пока профиль
	// грузится — не показываем (иначе мигал бы у залогиненных).
	const auth = useAuth()
	// «Регистрация»/«Войти» гостя — окном поверх карточки (владелец 11.10: тренд остаётся за окном)
	const authLink = useAuthLink()
	const isGuest = !auth.loading && !auth.user
	const [styleOpen, setStyleOpen] = useState(false)
	// Компактный вид — ТОЛЬКО по кнопке в шапке виджета (владелец, 25.09:
	// «слегка уменьшать габариты, но только отдельной кнопкой на виджете и
	// только на кастомном, а не на стандартном»). Автоматически от высоты
	// окна виджет не сжимается: «в виджете всё должно быть видно». Выбор
	// помнится в localStorage, как свёрнутость бара.
	const [compact, setCompact] = useState(() => {
		try {
			return localStorage.getItem(GENERATE_TOOL_COMPACT_KEY) === 'true'
		} catch {
			return false
		}
	})
	const toggleCompact = () => {
		setCompact((prev) => {
			const next = !prev
			try {
				if (next) localStorage.setItem(GENERATE_TOOL_COMPACT_KEY, 'true')
				else localStorage.removeItem(GENERATE_TOOL_COMPACT_KEY)
			} catch {
				/* приватный режим — вид живёт до перезагрузки */
			}
			return next
		})
	}
	// Пикер источника файла у слотов («Загрузить / Из моих генераций») —
	// единый механизм с классическим баром (см. useUploadPickerMenu).
	const { makeShowPicker, pickerPortal, galleryModal } = useUploadPickerMenu({
		hideCharacter: card?.hide_character,
		photoLimit: card?.user_photo_limit,
		// Пайп тренда и «Повторить движение»: «Аватар» в меню слота открывает окно аватаров в режиме героя — его ловит
		// свой ScenePickerHost pipeOnly (UploadsRow с обычным хостом тут не смонтирован).
		pipe: !!card?.pipelineSlug || isMotionCard(card),
	})

	// ── Пайплайн-режим (тренды «фото → видео»): карточка несёт pipelineSlug —
	// вместо одиночной генерации GTM ведёт этапы job'а бэка (usePipelineFlow):
	// фото → версии («ещё» копит, выбор кликом) → видео из выбранной.
	const isPipeline = !!card?.pipelineSlug
	// «Повторить движение» (13.10): карточка вида 'motion' — своя раскладка зон (ролик с отрезком · кто повторит ·
	// результат); входы и запуск держит useMotionMode, штатные UploadsRow и демо карточки тут не рисуются.
	const isMotion = isMotionCard(card)
	const motion = useMotionMode({ enabled: isMotion, cardId: card?.id ?? null, isGuest })
	// Предохранитель по балансу (стандарт utils/credits): цена — у тренда шага, у инструмента текущая.
	const guard = useCreditsGuard()
	const pipe = usePipelineFlow(card?.pipelineSlug)
	const [pipeResultOpen, setPipeResultOpen] = useState(false)
	// Фото юзера для пайплайна — СВОЙ канал слотов (не модельные слоты
	// UploadsRow): вход тренда задаёт админ в этапах (сколько фото и подпись),
	// карточкина нейронка для этого не нужна и слоты от неё не зависят.
	const pipeMax = pipe.photoField?.max_count ?? 2
	const [pipePhotos, setPipePhotos] = useState<UploadedFile[]>([])
	const pipeLastRejectRef = useRef<string | null>(null)
	const [pipeUploading, setPipeUploading] = useState(false)
	const handlePipePhotos = async (e: React.ChangeEvent<HTMLInputElement>) => {
		const files = Array.from(e.target.files || []).slice(0, Math.max(0, pipeMax - pipePhotos.length))
		if (e.target) e.target.value = ''
		if (!files.length) return
		// Фото первого этапа (картинка) — та же подготовка, что у слотов виджета (utils/imagePrepare): упаковка
		// (.jfif, HEIC) и вес; своих рамок у фото-моделей этапа пока нет — размеры не трогаем.
		// Свой прошлый отказ снимает новая попытка — раз на пачку (иначе подходящий файл стёр бы отказ соседа)
		// и только если на плашке всё ещё наш текст: чужие ошибки не трогаем.
		const shown = pipeLastRejectRef.current
		if (shown) {
			pipeLastRejectRef.current = null
			gen.setError((cur) => (cur === shown ? null : cur))
		}
		// Загрузка видна сразу, пока фото читается.
		setPipeUploading(true)
		const added: UploadedFile[] = []
		for (const raw of files) {
			const res = await prepareImageInput(raw, null, { maxMb: UPLOAD_SIZE_MB.image })
			if (!res.ok) {
				if (!res.cancelled) {
					gen.setError(res.message)
					pipeLastRejectRef.current = res.message
				}
				continue
			}
			if (res.note) showToast(res.note, 'info')
			added.push({ file: res.file, preview: URL.createObjectURL(res.file), note: res.note })
		}
		if (!added.length) {
			setPipeUploading(false)
			return
		}
		setPipePhotos((prev) => [...prev, ...added])
		try {
			for (const img of added) {
				const url = await uploadAndGetUrl(img.file, 'images')
				setPipePhotos((prev) => prev.map((i) => (i.preview === img.preview ? { ...i, url } : i)))
			}
		} catch {
			// Провал аплоада: убираем «зависшие» тумбы без url (успевшие — остаются).
			setPipePhotos((prev) => prev.filter((i) => i.url || !added.some((a) => a.preview === i.preview)))
		} finally {
			setPipeUploading(false)
		}
	}
	// ── Персонаж в слоте тренда (шаг 6). Данные — свой канал (фото уезжает в
	// config job'а атрибуцией character_id/name/photo_urls и восстанавливается
	// после F5), но ВЫБОР зеркалится в общий стор сцены (mode 'PIPE'): капсула
	// «Персонаж» в шапке — ЕДИНАЯ точка входа/статуса для всех каналов
	// (рассинхрон «в шапке пусто, в теле бейдж» вскрыл владелец). Герой — свой
	// собранный персонаж или библиотечный (Ф2 «Библиотека сцены»).
	const [pipeChar, setPipeCharRaw] = useState<{ id: string; name: string; urls: string[]; avatar?: string | null } | null>(null)
	const setPipeChar = (v: { id: string; name: string; urls: string[]; avatar?: string | null } | null) => {
		setPipeCharRaw(v)
		if (v) {
			setScene({
				host: 'pipe',
				assets: [{ id: v.id, kind: 'character', name: v.name, avatarUrl: v.avatar ?? null, isStock: false }],
				mode: 'PIPE',
				warnings: [],
				blocked: false,
				channel: 'uploaded',
				placedUrls: v.urls,
				placedBy: { [v.id]: v.urls },
				wordsOnly: [],
			})
			// «Липкость» и в пайпе: выбор переживает смену тренда и F5
			// (restore-эффект ниже); гасится только явным «×» юзера. Остальная
			// сцена (локации, предметы) в липкости остаётся — вернётся в обычном виджете.
			const cur = getStickyScene()
			setStickyScene(cur?.ids.includes(v.id) ? cur.ids : [v.id, ...(cur?.ids ?? [])], cur?.prefer, cur?.paths)
		} else if (getScene()?.mode === 'PIPE') {
			// Чистим ТОЛЬКО своё зеркало: обычный канал (UploadsRow) не трогаем.
			clearScene()
		}
	}
	const applyCharacterToPipe = async (characterId: string | string[], quiet = false) => {
		const ids = Array.isArray(characterId) ? characterId : [characterId]
		try {
			// task=trend: раскладку решает резолвер наборов (И4) — герой, один слот;
			// модель этапа не нужна (шаблон её скрывает). Тот же путь, что у сцены:
			// свой собранный персонаж (эталон) и библиотечный (главный вид).
			const plan = await planAssets({ asset_ids: ids, task: 'trend' })
			// Герой — первый персонаж набора (резолвер тренда); локация или предмет в слот героя не встают.
			const item = plan.layout[0]
			const hero = plan.assets.find((a) => a.id === item?.asset_id && a.kind === 'character')
			if (!plan.fits || !hero) {
				if (!quiet) {
					// Тексты причин — общий словарь UNFIT_TEXTS (NOT_BUILT — незавершённый персонаж), чтобы
					// формулировки не разъезжались по экранам.
					const asked = plan.assets.find((a) => ids.includes(a.id))
					const reason = asked && asked.status !== 'ready' ? 'NOT_BUILT' : plan.reason ?? ''
					showToast(UNFIT_TEXTS[reason] ?? 'Аватар не подходит этому тренду', 'info')
				}
				return
			}
			const url = item?.url
			if (!url) {
				showToast('У аватара нет фото для тренда', 'info')
				return
			}
			// «Сменить»: прежнее фото ЭТОГО персонажа уходит, ручные — целы.
			const base = pipeChar
				? pipePhotos.filter((p) => !p.url || !pipeChar.urls.includes(p.url))
				: pipePhotos
			if (base.length >= pipeMax) {
				showToast('Слоты фото заняты — освободите один для аватара', 'info')
				return
			}
			// file — заглушка (фото уже в S3, повторно не грузится), как у restore.
			setPipePhotos([...base, { file: new File([], 'character'), preview: url, url }])
			setPipeChar({
				id: hero.id,
				name: hero.name,
				urls: [url],
				avatar: assetThumb(hero),
			})
		} catch (e) {
			showToast(`Не удалось применить аватар: ${parseAssetError(e)}`, 'error')
		}
	}
	// Забыть героя в «липкости»: юзер снял его сам («×» капсулы или «Удалить» на его фото в слоте).
	// Карточка, в которой юзер сам снял героя: до смены тренда слот сам не заполняется. Иначе место снятого тут же
	// занимал следующий персонаж липкой сцены — «удалил, а он вернулся другим».
	const heroDismissedFor = useRef<string | null>(null)
	const forgetPipeHero = (id: string) => {
		heroDismissedFor.current = card?.id ?? null
		const cur = getStickyScene()
		if (cur) setStickyScene(cur.ids.filter((x) => x !== id), cur.prefer, cur.paths)
	}
	const removePipePhoto = (idx: number) => {
		const p = pipePhotos[idx]
		// Снято фото героя → снимаем атрибуцию И «липкость»: без этого эффект восстановления ниже тут же
		// возвращал героя в опустевший слот — «Удалить» на его фото не срабатывал (как и в обычном виджете,
		// ручное снятие фото = юзер убрал ассет).
		if (p?.url && pipeChar?.urls.includes(p.url)) {
			forgetPipeHero(pipeChar.id)
			setPipeChar(null)
		}
		setPipePhotos((prev) => {
			const q = prev[idx]
			if (q?.preview) URL.revokeObjectURL(q.preview)
			return prev.filter((_, i) => i !== idx)
		})
	}
	// «×» капсулы шапки шлёт SCENE_REMOVE_EVENT; в пайпе штатный слушатель
	// (useSceneApply из UploadsRow) не смонтирован — снимаем сами: фото
	// персонажа уходит из слота, зеркало стора чистит setPipeChar(null).
	const pipeClearRef = useRef<() => void>(() => {})
	pipeClearRef.current = () => {
		if (!pipeChar) return
		// Явное снятие юзером — выход из «липкости» героя (остальная сцена остаётся).
		const idx = pipePhotos.findIndex((p) => p.url && pipeChar.urls.includes(p.url))
		if (idx >= 0) removePipePhoto(idx) // снимет и «липкость»
		else {
			forgetPipeHero(pipeChar.id)
			setPipeChar(null)
		}
	}
	useEffect(() => {
		if (!isPipeline) return
		const h = () => pipeClearRef.current()
		window.addEventListener(SCENE_REMOVE_EVENT, h)
		return () => window.removeEventListener(SCENE_REMOVE_EVENT, h)
	}, [isPipeline])
	const pipePhotoUrls = useMemo(
		() => pipePhotos.map((p) => p.url).filter((u): u is string => !!u).slice(0, pipeMax),
		[pipePhotos, pipeMax],
	)
	// Атрибуция едет в config ТОЛЬКО если фото персонажа реально среди
	// отправляемых (removePipePhoto снимает и так; это страховка от гонок).
	// Сверка по path — подпись presigned меняется, путь стабилен.
	const pipeCharActive = useMemo(() => {
		if (!pipeChar) return null
		const paths = pipePhotoUrls.map((u) => u.split('?', 1)[0])
		return pipeChar.urls.every((u) => paths.includes(u.split('?', 1)[0])) ? pipeChar : null
	}, [pipeChar, pipePhotoUrls])
	const pipePending = pipeUploading || pipePhotos.some((p) => !p.url)
	// От СТАТУСА шага, не от phase: при фейле видео-этапа кнопка должна
	// остаться «Создать видео» (повторный запуск), а не «Создать фото».
	const pipePhotoDone = pipe.photoStep?.status === 'completed'
	// Лоадер в зоне результата обязан гореть С МОМЕНТА нажатия «Создать», а не
	// с появления job: pipe.phase считается по ответу сервера, и пока запрос
	// летит (создание job — секунды), фаза оставалась 'idle' — юзер видел
	// пустую рамку при уже запущенной генерации (жалоба владельца 2026-07-24).
	// pipe.busy = «наш запрос в полёте» (createPhoto/createVideo/retry).
	const pipeProcessing = pipe.phase === 'photo_processing' || pipe.phase === 'video_processing'
	const pipeWorking = pipeProcessing || pipe.busy
	// «Начать заново»: полный сброс прогона — job (версии/видео) и фото юзера.
	// Оплаченные генерации не пропадают — они уже в «Моих генерациях».
	const handlePipeReset = () => {
		pipe.resetJob()
		setPipeChar(null)
		setPipePhotos((prev) => {
			prev.forEach((p) => p.preview && URL.revokeObjectURL(p.preview))
			return []
		})
	}

	// Вариации стиля из пина (переживают F5; VariantContext на reload пуст).
	const pinVariants = card?.demoVariants ?? []
	// Живой источник chips — VariantContext; fallback на пин, чтобы после F5
	// стиль-селект не пропадал. Активная — из контекста, иначе из пина.
	const chips = variant.variantChips.length
		? variant.variantChips
		: pinVariants.map((v) => ({
				key: v.key,
				label: v.label,
				patch: v.prompt_patch,
				isOpen: v.is_open,
				placeholder: v.placeholder,
				afterUrl: v.after_url,
				valueType: v.value_type,
				minValue: v.min_value,
				maxValue: v.max_value,
				userInput: v.user_input ?? null,
			}))
	const activeKey = variant.activeVariantKey || card?.activeVariantKey || chips[0]?.key
	const activeChip = chips.find((c) => c.key === activeKey) ?? chips[0]

	// «Свой стиль»: активная вариация — open (юзер вписывает значение).
	// Значение живёт локально (печать без ре-рендеров подписчиков пина) и
	// зеркалится в пин для F5 (setAttachedCustomValue, без emit).
	const isCustomActive = !!activeChip?.isOpen
	const [customValue, setCustomValue] = useState(card?.customValue ?? '')
	const applyCustom = (v: string) => {
		setCustomValue(v)
		variant.selectCustomVariant(activeKey, v) // {value}→v, промпт под капотом
		setAttachedCustomValue(v) // персист для F5
	}
	// Смена карточки → значение прошлой не утекает в новую (компонент не
	// ремоунтится при переходе карточка→карточка). Фото пайплайн-слотов —
	// той же судьбы: чужому тренду они не принадлежат.
	const cardId = card?.id
	useEffect(() => {
		setCustomValue(getAttachedStyleCard()?.customValue ?? '')
		// Смена версии мульти-тренда с той же формой загрузок (23.10): фото этапов и выбранный герой остаются.
		if (getAttachedStyleCard()?.keepUploads) return
		// Новый тренд — «снял героя сам» больше не действует: липкий персонаж снова встаёт в слот.
		heroDismissedFor.current = null
		setPipeChar(null)
		setPipePhotos((prev) => {
			prev.forEach((p) => p.preview && URL.revokeObjectURL(p.preview))
			return []
		})
	}, [cardId])

	// «Липкость» и в пайпе: слоты пусты (новый тренд / F5 без job'а), липкий
	// выбор есть → авто-переприменение (свежий /plan — presigned не протухает).
	// Провал (unfit-тост) не зацикливается: deps после него не меняются.
	useEffect(() => {
		if (!isPipeline || pipeChar || pipePhotos.length) return
		if (heroDismissedFor.current && heroDismissedFor.current === (cardId ?? null)) return
		// В пайпе герой один: первый персонаж липкой сцены (восстановление — без тостов).
		const sticky = getStickyScene()
		if (sticky?.ids.length) void applyCharacterToPipe(sticky.ids, true)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isPipeline, cardId, pipeChar, pipePhotos.length])

	// Восстановление слота после F5: job переживает перезагрузку (sessionStorage
	// → бэк), а pipePhotos (локальный state) — нет. Когда job поднялся, а слот
	// пуст — заполняем фото, из которых собран job (config.user_photos; бэк
	// отдаёт их свежим presigned, не протухают). Один раз на job (ref) — ручное
	// удаление фото из слота (или очистка виджета) не перетирается обратно.
	const restoredJobRef = useRef<string | null>(null)
	useEffect(() => {
		const jobId = pipe.job?.id
		if (!jobId) {
			restoredJobRef.current = null
			return
		}
		// Гонка при смене карточки: pipe.job ещё старый (другой slug) — не
		// тащим чужие фото в новую карточку. Ждём job текущего тренда.
		if (pipe.job?.pipeline_type !== card?.pipelineSlug) return
		if (restoredJobRef.current === jobId || pipePhotos.length > 0) return
		const key = pipe.photoField?.key || 'user_photos'
		const raw = (pipe.job?.config as Record<string, unknown> | undefined)?.[key]
		const urls = Array.isArray(raw) ? raw.filter((u): u is string => typeof u === 'string') : []
		if (!urls.length) return
		restoredJobRef.current = jobId
		// file — заглушка (восстановленные не грузятся повторно, у них есть url);
		// preview = сам presigned URL (рендерится в тумбе и Tooltip-превью).
		setPipePhotos(urls.map((u) => ({ file: new File([], 'restored'), preview: u, url: u })))
		// Атрибуция персонажа пережила F5 в config (бэк пере-подписал
		// character_photo_urls свежими заодно с user_photos — сверка «какие из
		// слотов персонажьи» остаётся строковой).
		const cfg = pipe.job?.config as Record<string, unknown> | undefined
		const charId = typeof cfg?.character_id === 'string' ? cfg.character_id : null
		const charName = typeof cfg?.character_name === 'string' ? cfg.character_name : ''
		const charUrls = Array.isArray(cfg?.character_photo_urls)
			? (cfg.character_photo_urls as unknown[]).filter((u): u is string => typeof u === 'string')
			: []
		if (charId && charUrls.length) setPipeChar({ id: charId, name: charName, urls: charUrls })
	}, [pipe.job, pipe.photoField, pipePhotos.length, card?.pipelineSlug])

	// Слайдер: before = исходник карточки, after = активная вариация.
	const activeVariant = pinVariants.find((v) => v.key === activeKey) ?? pinVariants[0]
	const beforeUrl = card?.demoBeforeUrl
	// Пара «до/после» без вариаций: after из карточки (вариации главнее).
	const afterUrl = activeVariant?.after_url ?? card?.demoAfterUrl
	// Мультистиль (владелец 2026-08-14): НЕ «до/после» — демо-зона показывает
	// крупно ВЫБРАННЫЙ стиль + ряд свотчей-переключателей под ним.
	const styleChips = chips.filter((c) => !c.isOpen)
	// «Свой вариант» в ряду стилей мультистиля (владелец 2026-08-21, третий
	// сценарий): open-вариации — самостоятельные чипы В ХВОСТЕ ряда. Юзер
	// выбирает его как стиль и вписывает своё значение; на промпты других
	// стилей это не влияет (механика та же, что у open-опции в селекте).
	const openChips = chips.filter((c) => c.isOpen)
	// «Мягкие края» ряда свотчей: fade-классы вешаются по scrollLeft (без
	// React-стейта — classList, чтобы скролл не дёргал ре-рендеры).
	const mstyRowRef = useRef<HTMLDivElement>(null)
	const updateMstyFade = useCallback(() => {
		const el = mstyRowRef.current
		if (!el) return
		el.classList.toggle('gtm__msty-row--fade-l', el.scrollLeft > 4)
		el.classList.toggle('gtm__msty-row--fade-r', el.scrollLeft < el.scrollWidth - el.clientWidth - 4)
	}, [])
	useEffect(() => {
		updateMstyFade()
	}, [updateMstyFade, styleChips.length])
	// Ряды свотчей (стили мультистиля, участники мульти-тренда): перетягивание мышкой на десктопе и «мягкие края» —
	// одна механика на оба ряда.
	const startRowDrag = (e: React.MouseEvent<HTMLDivElement>) => {
		// Десктоп: перетягивание ряда мышкой (владелец 2026-08-16).
		// Клик по свотчу не глушим: скролл начинается только после
		// сдвига > 4px, до того mouseup доходит как обычный клик.
		const row = e.currentTarget
		const startX = e.pageX
		const startLeft = row.scrollLeft
		let moved = false
		const onMove = (ev: MouseEvent) => {
			const dx = ev.pageX - startX
			if (Math.abs(dx) > 4) moved = true
			if (moved) row.scrollLeft = startLeft - dx
		}
		const onUp = (ev: MouseEvent) => {
			window.removeEventListener('mousemove', onMove)
			window.removeEventListener('mouseup', onUp)
			// Дотащили — гасим клик, чтобы не сработал выбор свотча.
			if (moved) {
				const swallow = (ce: Event) => { ce.stopPropagation(); ce.preventDefault() }
				row.addEventListener('click', swallow, { capture: true, once: true })
				setTimeout(() => row.removeEventListener('click', swallow, { capture: true }), 0)
			}
			void ev
		}
		window.addEventListener('mousemove', onMove)
		window.addEventListener('mouseup', onUp)
	}
	const updateRowFade = (el: HTMLDivElement | null) => {
		if (!el) return
		el.classList.toggle('gtm__msty-row--fade-l', el.scrollLeft > 4)
		el.classList.toggle('gtm__msty-row--fade-r', el.scrollLeft < el.scrollWidth - el.clientWidth - 4)
	}
	const isMsty = card?.card_kind === 'multistyle' && styleChips.length > 0
	// Мульти-тренд (22.10): в пине — участник группы, группа — рядом. Ряд свотчей участников под демо; клик —
	// лендинг группы с этим участником (useToolCardLanding применит его: промпт, референсы, цена — его).
	// С /generate тот же адрес уводит в раздел: применять там некому.
	const location = useLocation()
	const trendGroup = card?.trendGroup
	// Без адреса лендинга переключать некуда — ряд не рисуем (клик в никуда хуже отсутствия ряда).
	const trendMembers = trendGroup?.path && trendGroup.members.length > 1 ? trendGroup.members : []
	// Пока идёт генерация или этап пайплайна — смена участника заперта: применение карточки сбросило бы слоты и
	// результат на полпути.
	const trendSwitchLocked = gen.isLoading || pipeWorking
	const pickTrendMember = (id: string) => {
		if (!trendGroup?.path || id === card?.id || trendSwitchLocked) return
		navigate(trendGroup.path, {
			replace: location.pathname + location.search === trendGroup.path,
			state: { trendMemberId: id, skipAnchor: true },
		})
	}
	const trendRowRef = useRef<HTMLDivElement>(null)
	useEffect(() => {
		updateRowFade(trendRowRef.current)
	}, [trendMembers.length])
	const trendRow = trendMembers.length > 0 ? (
		<div
			ref={trendRowRef}
			className="gtm__msty-row gtm__trend-row"
			role="tablist"
			aria-label="Тренды группы"
			onScroll={(e) => updateRowFade(e.currentTarget)}
			onMouseDown={startRowDrag}
		>
			{trendMembers.map((m) => (
				<button
					key={m.id}
					type="button"
					role="tab"
					aria-selected={m.id === card?.id}
					className={`gtm__msty-swatch gtm__trend-swatch${m.id === card?.id ? ' is-on' : ''}`}
					onClick={() => pickTrendMember(m.id)}
					disabled={trendSwitchLocked}
					title={m.title}
				>
					{m.previewUrl ? <img src={m.previewUrl} alt={m.title} loading="lazy" /> : m.title.slice(0, 2)}
				</button>
			))}
		</div>
	) : null

	// Видео-карточка с фото-слотами: слоты уходят референсами → Seedance обязан
	// быть в режиме «Референсы» (в «Кадрах» payload раскидал бы первые два фото
	// по first/last_frame). Матчим по МОДЕЛИ: kind у пина — атрибуция
	// ('card'|'style'|'video'-витрина), НЕ тип генерации (живой баг 2026-08-20 —
	// сводка показывала «Кадры»). Пилюля переключения при слотах спрятана.
	const cardWantsRefs =
		hasFrameRefModes(gen.selectedModel) &&
		(card?.photo_slots?.length ?? 0) >= 2
	useEffect(() => {
		if (!cardWantsRefs) return
		if (gen.settings.seedanceMode !== 'references') gen.updateSetting('seedanceMode', 'references')
	}, [cardWantsRefs, gen.settings.seedanceMode]) // eslint-disable-line react-hooks/exhaustive-deps
	const hasSlider = !isMsty && !!beforeUrl && !!afterUrl

	// Поля входов НЕ дублируются: их рендерит UploadsRow — единый источник
	// правды классического бара (фото/видео/аудио-референсы, Seedance-режимы,
	// два фото, конечный кадр — вся матрица модели). needsVideo — только для
	// авто-подстановки промптового видео ниже.
	const needsVideo = gen.requiresVideo

	// Именованные фото-слоты карточки: «Создать» ждёт ВСЕ слоты — промпт
	// командует «первым/вторым фото», без любого из них он врёт модели.
	// Условие активности зеркалит UploadsRow.activeNamedSlots.
	const cardSlots = card?.photo_slots
	// Мультистиль: набор слотов зависит от ВЫБРАННОГО стиля ({photoN} в его
	// промпте; владелец 2026-08-16). До restored-эффекта контекст пуст —
	// считаем по пин-чипу и базе из пина. null = фильтра нет (все слоты).
	const activeSlotNums = variant.variantChips.length
		? variant.activePhotoSlotNums
		: chips.length
			? usedPhotoSlotNums((activeChip?.patch ?? '') + ' ' + (card?.demoBasePrompt ?? ''))
			: null
	// Вместимость — общее правило сайта и конструктора (photo-slots/photoSlotCapacity): у Seedance 2.x не меньше девяти.
	const namedSlotsActive =
		!!cardSlots && cardSlots.length >= 2 &&
		photoSlotCapacity(gen.selectedModel) >= cardSlots.length && !gen.requiresTwoImages
	const namedSlotsMissing =
		namedSlotsActive &&
		cardSlots.some((_, i) => (activeSlotNums == null || activeSlotNums.includes(i + 1)) && !gen.slotImages[i]?.url)

	// Карточное поле значения: активный стиль подставляет {value}, а значение
	// пусто → «Создать» блокируется (владелец 2026-08-16: пустое поле = дырка
	// в промпте; min из админки — только плейсхолдер, не значение).
	// Конфиг поля активного стиля: своё поле стиля перекрывает общее
	// (владелец 2026-08-16). До restored-эффекта — по пин-чипу.
	const activeUserInputGtm = variant.variantChips.length
		? variant.activeUserInput
		: activeChip?.userInput ?? card?.user_input ?? null
	const activeUsesValueGtm =
		!!activeUserInputGtm &&
		(variant.variantChips.length
			? variant.activeUsesValue
			: /\{value\}/.test(activeChip?.patch ?? '') || /\{value\}/.test(card?.demoBasePrompt ?? ''))
	const pinValueKey = activeChip?.userInput ? activeChip.key : '__card__'
	// «Свой вариант» (open-чип): {value} в его патче занят СОБСТВЕННЫМ вводом
	// (customValue), а не общим полем — валидацию общего значения не включаем
	// (симметрично скрытию самого поля в баре ниже).
	const toolCost = isPipeline ? (pipePhotoDone ? pipe.videoCost : pipe.photoCost) : gen.currentCost
	// Инструмент на модели с бесплатным тиром: пока остаток есть, сервер спишет 0 — не блокируем (как в баре виджета).
	// Шаги тренда списываются по цене шаблона без бесплатного тира — там цена точная.
	const toolModel = gen.selectedModel
	const toolFreeType = !isPipeline && toolModel ? (toolModel.category === 'video' ? 'video' : toolModel.category === 'image' ? 'image' : null) : null
	const toolFreeParams = toolModel && toolFreeType
		? (toolFreeType === 'video'
			? { duration: gen.settings.duration, resolution: gen.settings.resolution, aspect_ratio: gen.settings.aspectRatio === 'match_input_image' ? null : gen.settings.aspectRatio }
			: { resolution: gen.settings.resolution, quality: toolModel.qualityLevels ? gen.settings.quality : null, aspect_ratio: gen.settings.aspectRatio === 'match_input_image' ? null : gen.settings.aspectRatio, output_format: gen.settings.outputFormat })
		: null
	// Только вошедшему: ручка остатка гостю отвечает 401, а клиент на 401 уводит на /login — гость не увидел бы карточку
	// и заглушку «Войдите, чтобы создать» (08.10–11.10 так уводило со всех страниц карточек).
	const toolFreeTier = useModelPricing(toolModel?.provider, toolModel ? (toolModel.backendModel || toolModel.id) : null, toolFreeType, toolFreeParams, gen.isLoading, !!auth.user)
	// Плата за секунды входного видео: пока сервер меряет видео на входе (или не смог), цены со входом нет — запуск ждёт.
	const toolCostHold = !isPipeline && (gen.costPending || !!gen.costIssue)
	const toolShort = !toolCostHold && !hasFreeLeft(toolFreeTier) && guard.shortFor(toolCost)
	const userValueMissing =
		!isCustomActive &&
		activeUsesValueGtm &&
		!(variant.variantChips.length
			? variant.cardUserValue
			: (card?.userValues?.[pinValueKey] ?? (pinValueKey === '__card__' ? card?.userValue : '') ?? '')
		).trim()
	// «Свой вариант» без текста: промпт остался бы с {variant} (аудит 22.10 — такой запрос уходил и списывался).
	const customValueMissing = isCustomActive && !customValue.trim()

	// Инструмент с фото-входом без единого фото: кнопку «Создать» дизейблим
	// (модели с опциональным image-входом типа GPT Image охотно генерят и без
	// фото — но инструмент «поменяй фон/улучши фото» без исходника бессмыслен).
	const noPhotoYet =
		((gen.requiresImage || gen.supportsImageInput) &&
			!gen.uploadedImage &&
			gen.referenceImages.length === 0 &&
			!gen.swapImage &&
			gen.omniReferenceImages.length === 0 &&
			!gen.endImage) ||
		// Модели на ДВА снимка: условие выше «хоть один канал заполнен» их
		// пропускало — с одной картинкой кнопка горела, запрос уходил, а бэк
		// (`_face_swap`: data.clear() при len(images) < 2) получал ПУСТОЙ вход и
		// сжигал токены впустую. Тут правило строгое — как в классическом баре.
		(gen.requiresTwoImages && (!gen.uploadedImage?.url || !gen.swapImage?.url)) ||
		namedSlotsMissing ||
		userValueMissing ||
		customValueMissing

	// Соотношение рамки результата. Тренд: формат ЭТАПА (9:16 из шаблона), а не
	// глобальный gen.settings (дефолт 1:1 letterbox'ил 9:16-контент). Во время
	// видео-фазы — формат видео-этапа (может отличаться от фото-этапа).
	const pipeShowsVideo = pipe.phase === 'video_processing' || pipe.phase === 'video_ready'
	const arSource = isPipeline
		? (pipeShowsVideo ? pipe.videoAspect : pipe.photoAspect) ?? pipe.photoAspect ?? pipe.videoAspect
		: gen.settings.aspectRatio
	// «Повторить движение»: ролик выходит в формате входа (у Seedance и замены формат «как у референса», выбора размера
	// нет) — рамка результата в пропорции самого ролика из плеера (владелец 14.10); до ролика — портрет, как у роликов с
	// движением, а не формат из настроек нейросети.
	const motionAspect = isMotion ? (motion.clipAspect ?? 9 / 16) : null
	const arCss = motionAspect != null ? String(Math.round(motionAspect * 1000) / 1000) : (AR_CSS[arSource ?? ''] ?? '3 / 4')
	// Мобильная сетка зон (скетч владельца): узкий/квадратный формат — «демо |
	// результат» парой колонок (кадр целиком, без кропа); широкий (>1.2) — те
	// же блоки стопкой на всю ширину, иначе 16:9 в колонке — полоска-крошка.
	const arWide = motionAspect != null ? motionAspect > 1.2 : (() => {
		const [w, h] = (arSource ?? '').split(':').map(Number)
		return !!w && !!h && w / h > 1.2
	})()

	// Смена вариации → промпт под капотом. Поле промпта скрыто, а печатающий
	// консьюмер promptAnim живёт в GenerateInputBar (не смонтирован). Переносим
	// выбранную вариацию в gen.prompt напрямую — мгновенно, с тем же stale-guard,
	// что и бар: устаревший запуск после смены карточки (latestNonce=-1) не
	// перетрёт свежий промпт. Начальную вариацию ставит applyCardToGenerate.
	useEffect(() => {
		const pa = variant.promptAnim
		if (pa && pa.nonce === variant.latestNonce.current) gen.setPrompt(pa.target)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [variant.promptAnim?.nonce])

	// Восстановление из пина: после F5 (контекст пуст) и при смене карточки, когда контекст держит ЧУЖУЮ (22.10:
	// раньше только на маунте и только при пустом контексте — окно показывало стили прежней карточки). Свою карточку
	// (чипы поставило применение) не трогаем. Заодно восстанавливаем промпт (у гостя пусто) — со значением поля из
	// пина (variantFieldValue; раньше читалось только легаси userValue, и после F5 в промпт уходила пустота).
	useEffect(() => {
		if (!pinVariants.length || !card) return
		if (variant.variantCardId === card.id || variant.variantCardId === `popular-${card.id}`) return
		const restored = pinVariants.map((v) => ({
			key: v.key,
			label: v.label,
			patch: v.prompt_patch,
			isOpen: v.is_open,
			placeholder: v.placeholder,
			afterUrl: v.after_url,
			valueType: v.value_type,
			minValue: v.min_value,
			maxValue: v.max_value,
			userInput: v.user_input ?? null,
		}))
		const base = card.demoBasePrompt ?? ''
		const key =
			card.activeVariantKey && restored.some((c) => c.key === card.activeVariantKey)
				? card.activeVariantKey
				: restored[0].key
		variant.setVariantCard(restored, base, `popular-${card.id}`, key, card.user_input ?? null, card.userValues ?? (card.userValue ? { __card__: card.userValue } : {}))
		if (!card.promptLocked) {
			const restoredActive = restored.find((c) => c.key === key)
			// «Свой стиль»: восстановленная активная вариация — open и есть
			// сохранённое значение → патч с подстановкой {value} (иначе патч
			// open-вариации ушёл бы в промпт сырым шаблоном).
			if (restoredActive?.isOpen) {
				const trimmed = (card.customValue ?? '').trim()
				const patch = trimmed
					? (restoredActive.patch ?? '').replace(/\{value\}/g, trimmed)
					: ''
				gen.setPrompt(substitutePhotoSlots(buildVariantPrompt(base, patch).replace(/\{value\}/g, ''), gen.selectedModel?.category === 'video' ? 'video' : 'image'))
			} else {
				gen.setPrompt(substitutePhotoSlots(buildVariantPrompt(base, restoredActive?.patch ?? '').replace(/\{value\}/g, variantFieldValue(card, key)), gen.selectedModel?.category === 'video' ? 'video' : 'image'))
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps -- по смене карточки пина; остальное читаем на момент смены
	}, [card?.id])

	// Медиа-референсы инструмента «под капотом» (админ-настройка карточки).
	// ВИДЕО — источники по приоритету:
	//   1. referenceVideoUrl — ЯВНЫЙ референс из админки (поле «Видео-референс»
	//      карточки/витрины): канал зависит от модели — обязательный вход
	//      (uploadedVideo) либо референс-канал (referenceVideo / referenceVideos,
	//      те же условия, что рендер слотов в UploadsRow);
	//   2. videoUrl (демо-ролик карточки) — эвристика ТОЛЬКО для двувходных
	//      моделей (Motion Control: фото + видео), где ролик = шаблон движения.
	// Чистые video-to-video без явного референса не трогаем: там видео —
	// контент юзера. Подстановка лёгкая (URL без скачивания: payload шлёт .url).
	// Срабатывает на входе/смене карточки при пустом канале; удалённый юзером
	// референс не возвращаем (стейты каналов нарочно не в deps).
	// В deps — эпоха сбросов: применение карточки после смены модели идёт через
	// applyCard → resetUploads и стирало уже подставленный референс, а эффект
	// без повода повторно не срабатывал — видео из админки доезжало до модели
	// только когда сброс успевал раньше подстановки (владелец 19.09: у «Шторм
	// Сторринг» 6 запросов из 20). Крестик в слоте — не сброс: эпоха не растёт,
	// снятый юзером референс не возвращается.
	// Ссылки на референс у клиента может не быть (19.09: её убрали из публичной витрины,
	// откуда мастер-файлы забирали curl'ом). Тогда канал занимаем короткой подписью —
	// она только для показа, в генерацию уходит флаг, а файл подставляет сервер.
	// Гостю подпись не выдают, и спрашивать её нельзя: ответ 401 уводил гостя с лендинга тренда на
	// страницу входа — стекло с «Войдите, чтобы создать» он так и не видел (30.09). После входа эффект
	// срабатывает снова (isGuest в зависимостях), и трек карточки встаёт в канал.
	const [signedRef, setSignedRef] = useState<{ video?: string; audio?: string }>({})
	useEffect(() => {
		const id = card?.id
		if (!id || isGuest) { setSignedRef({}); return }
		let alive = true
		const need: Array<'video' | 'audio'> = []
		if (card?.hasRefVideo && !card?.referenceVideoUrl) need.push('video')
		if (card?.hasRefAudio && !card?.referenceAudioUrl) need.push('audio')
		if (!need.length) { setSignedRef({}); return }
		void Promise.all(need.map((k) => cardReferenceUrl(id, k).then((url) => [k, url] as const)))
			.then((pairs) => {
				if (!alive) return
				setSignedRef(Object.fromEntries(pairs.filter(([, url]) => !!url)) as { video?: string; audio?: string })
			})
		return () => { alive = false }
	}, [card?.id, card?.hasRefVideo, card?.hasRefAudio, card?.referenceVideoUrl, card?.referenceAudioUrl, isGuest])

	const model = gen.selectedModel
	const frameRefModes = hasFrameRefModes(model)
	const refVideoChannel = model?.supportsReferenceVideo
		? (model?.maxReferenceVideos ?? 1) > 1
			? (!frameRefModes || gen.settings.seedanceMode === 'references' ? 'multi' : null)
			: 'single'
		: needsVideo
			? 'uploaded'
			: null
	// В режиме «Повторить движение» ролик — всегда свой: референсы карточки «под капотом» не подставляем.
	const autoVideoUrl = refVideoChannel && !isMotion
		? (card?.referenceVideoUrl ?? signedRef.video ??
			(refVideoChannel === 'uploaded' && gen.requiresImage ? card?.videoUrl : undefined))
		: undefined
	// Локи полей юзера (админ): видео/аудио-входы опциональны, поэтому лок
	// уважаем и БЕЗ админ-референса — «карточке эти слоты не нужны» (владелец
	// 2026-08-21). Тупика нет: генерация без видео/аудио валидна. У фото ниже
	// правило строже — там без референса лок игнорируется (обязательный вход).
	const videoLocked = !!card?.userVideoLocked
	const cardHasRefVideo = !!(card?.hasRefVideo || card?.referenceVideoUrl)
	const seedance25 = isSeedance25Model(model)
	// Видео тренда (shared/trend-video, 12.10): Seedance 2.5 его переделывает — формат и длина ролика как у видео, полей
	// нет, вместо них строка «Формат и длина — как у видео тренда: 16:9, 15 с». Виджету от карточки нужно только «видео
	// есть, скрыто ли» (settings.cardTrendVideo); переделывается ли оно, решает общее правило на живом состоянии — убрал ли
	// человек видео тренда, положил ли своё (referenceLimits.trendVideoFollows), так же как карточка тренда в админке и
	// сервер. Длину и кадр меряет сервер (ссылку на файл он не отдаёт); длина задаёт и цену — сервер спишет столько же.
	const [cardRefMeta, setCardRefMeta] = useState<{ id: string; meta: CardReferenceMeta | null } | null>(null)
	useEffect(() => {
		const id = card?.id
		if (!id || !cardHasRefVideo || !seedance25 || isGuest) return
		let alive = true
		void cardReferenceMeta(id).then((meta) => {
			if (alive) setCardRefMeta({ id, meta })
		})
		return () => { alive = false }
	}, [card?.id, cardHasRefVideo, seedance25, isGuest])
	const refMeta = cardRefMeta?.id === card?.id ? cardRefMeta?.meta : null
	const trendHidden = card && cardHasRefVideo ? videoLocked : undefined
	// «Убрал видео тренда» — только когда оно стояло в слоте и человек его снял. Пока ссылка на просмотр грузится (у гостя
	// её нет вовсе), видео в слоте ещё нет, но уйдёт оно в генерацию — выбор формата показывать нельзя.
	const cardVideoInSlot = gen.referenceVideos.some((v) => v.cardRef)
	const [cardVideoSeen, setCardVideoSeen] = useState<string | null>(null)
	useEffect(() => {
		if (cardVideoInSlot && card?.id && cardVideoSeen !== card.id) setCardVideoSeen(card.id)
	}, [cardVideoInSlot, card?.id, cardVideoSeen])
	const trendRemoved = !trendHidden && cardVideoSeen === card?.id && !cardVideoInSlot
	const refSec = refMeta?.seconds ?? undefined
	const refW = refMeta?.width ?? undefined
	const refH = refMeta?.height ?? undefined
	useEffect(() => {
		const s = gen.settings
		if (s.cardTrendVideo?.hidden !== trendHidden || !!s.cardTrendVideo?.removed !== (trendHidden != null && trendRemoved))
			gen.updateSetting('cardTrendVideo', trendHidden == null ? undefined : { hidden: trendHidden, removed: trendRemoved })
		if (s.cardReferenceSeconds !== refSec) gen.updateSetting('cardReferenceSeconds', refSec)
		if (s.cardReferenceFrame?.width !== refW || s.cardReferenceFrame?.height !== refH)
			gen.updateSetting('cardReferenceFrame', refW && refH ? { width: refW, height: refH } : undefined)
	}, [trendHidden, trendRemoved, refSec, refW, refH]) // eslint-disable-line react-hooks/exhaustive-deps
	// Длина в настройках = длине видео тренда, пока оно переделывается: пресет мог задать другую, смена модели сбрасывает.
	const trendState = {
		...gen.settings,
		cardTrendVideo: trendHidden == null ? undefined : { hidden: trendHidden, removed: trendRemoved },
		cardReferenceSeconds: refSec,
	}
	const trendSec = trendVideoFollows(model, trendState, gen.referenceVideos)
		? durationFollowsReference(model, trendState, gen.referenceVideos)
		: null
	useEffect(() => {
		if (trendSec != null && gen.settings.duration !== String(trendSec)) gen.updateSetting('duration', String(trendSec))
	}, [trendSec, model?.id]) // eslint-disable-line react-hooks/exhaustive-deps
	// Замок длительности тренда (карточка в админке: settings.locked, 11.10): тренд «через референсы» ломается от
	// другой длины — пилюля заперта на длине тренда, сервер другую не примет (video._trend_card_checked). Где длину
	// задаёт видео, замок не действует (referenceLimits.durationLock).
	const trendDuration = card?.settings?.locked?.includes('duration') ? Number(card.settings.duration) : NaN
	const durationLockSec = Number.isFinite(trendDuration) && trendDuration > 0 ? trendDuration : undefined
	useEffect(() => {
		if (gen.settings.cardDurationLock !== durationLockSec) gen.updateSetting('cardDurationLock', durationLockSec)
	}, [durationLockSec]) // eslint-disable-line react-hooks/exhaustive-deps
	// Длина в настройках = длине тренда: пресет её ставит при открытии, но смена модели сбрасывает настройки к своим.
	const trendLock = durationLock(model, { ...trendState, cardDurationLock: durationLockSec }, gen.referenceVideos ?? [],
		!!gen.uploadedVideo?.url)
	useEffect(() => {
		if (trendLock?.byTrend && gen.settings.duration !== String(trendLock.sec)) gen.updateSetting('duration', String(trendLock.sec))
	}, [trendLock?.byTrend, trendLock?.sec, gen.settings.duration, model?.id]) // eslint-disable-line react-hooks/exhaustive-deps
	useEffect(() => () => {
		gen.updateSetting('cardTrendVideo', undefined)
		gen.updateSetting('cardReferenceSeconds', undefined)
		gen.updateSetting('cardReferenceFrame', undefined)
		gen.updateSetting('cardDurationLock', undefined)
	}, []) // eslint-disable-line react-hooks/exhaustive-deps
	useEffect(() => {
		if (!autoVideoUrl) return
		if (refVideoChannel === 'uploaded' && !gen.uploadedVideo) gen.applyDefaultUploadedVideo(autoVideoUrl)
		else if (refVideoChannel === 'single' && !gen.referenceVideo) gen.applyDefaultReferenceVideo(autoVideoUrl)
		else if (refVideoChannel === 'multi' && !gen.referenceVideos.length) gen.applyDefaultReferenceVideos(autoVideoUrl)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [card?.id, autoVideoUrl, refVideoChannel, gen.uploadsEpoch])

	// ФОТО-референс «под капотом» (админ-настройка карточки). Уходит в ПЕРВЫЙ
	// слот, а собственное фото юзера остаётся во втором — для замены лица это
	// ровно то, что нужно: админ задаёт сцену, юзер приносит лицо.
	// Замок игнорируем, если референса нет ИЛИ юзеру не останется своего слота
	// (иначе тупик: полей нет, загрузить нечего).
	// УНИВЕРСАЛЬНОЕ условие «нейронка работает только с фото»: текст не читает
	// и ждёт два снимка. Никаких имён моделей — следующая такая модель получит
	// эту раскладку сама, достаточно флага noPrompt в её ui_metadata.
	const photoOnly = !!model?.noPrompt && !!gen.requiresTwoImages
	const userHasOwnSlot = !!(gen.requiresTwoImages || gen.supportsMultipleImages)
	const autoImageUrl = isMotion ? undefined : card?.referenceImageUrl
	// Пока фото скачивается — слот держим скрытым (иначе он мигнёт). После
	// попытки судим по РЕЗУЛЬТАТУ: applyDefaultUploadedImage глотает ошибки
	// молча (catch {} внутри), поэтому битая ссылка не бросит исключение —
	// просто ничего не появится. Без этой развилки замок оставил бы юзера с
	// одним слотом при обязательных двух: заполнить нечем, кнопка мертва.
	const [autoImageTried, setAutoImageTried] = useState(false)
	const imageLocked =
		!!card?.userImageLocked &&
		!!autoImageUrl &&
		userHasOwnSlot &&
		(!autoImageTried || !!gen.uploadedImage)
	useEffect(() => {
		if (!autoImageUrl) return
		// Связка: при именованных фото-слотах фото-канал занят слотами —
		// админ-референс в payload не попадает, не скачиваем его зря
		// (в админке этот блок замьючен с той же причиной).
		if ((card?.photo_slots?.length ?? 0) >= 2) return
		if (gen.uploadedImage) return
		let alive = true
		void (async () => {
			await gen.applyDefaultUploadedImage(autoImageUrl)
			if (alive) setAutoImageTried(true)
		})()
		return () => {
			alive = false
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [card?.id, autoImageUrl, gen.uploadsEpoch])

	// АУДИО-референс «под капотом» — только модели с аудио-каналом (тот же
	// гейт, что рендер MultiAudioRow: Seedance-2 в режиме «Кадры» аудио не ждёт).
	const autoAudioUrl =
		model?.supportsReferenceAudio && (!frameRefModes || gen.settings.seedanceMode !== 'frames')
			? (card?.referenceAudioUrl ?? signedRef.audio)
			: undefined
	const audioLocked = !!card?.userAudioLocked

	// Квадратики референсов на плитке демо (19.09). Рисуем ТОЛЬКО когда слота у канала
	// нет: карточка с именованными фото-слотами (2+) рисует лишь их, ряды видео/аудио в
	// такой раскладке не появляются вовсе — галочка «показ» там молча не работала.
	// Запертый канал (снятый «показ») не показываем: админ спрятал его сознательно.
	// Условие — как у activeNamedSlots в UploadsRow: два и больше слотов и модель не из
	// тех, что сами требуют пару фото (там своя раскладка и свои слоты).
	const namedSlotsLayout = (card?.photo_slots?.length ?? 0) >= 2 && !gen.requiresTwoImages
	const refThumbs: ToolReferenceItem[] = namedSlotsLayout
		? ([
			!videoLocked && autoVideoUrl ? { kind: 'video' as const, url: autoVideoUrl } : null,
			!audioLocked && autoAudioUrl ? { kind: 'audio' as const, url: autoAudioUrl } : null,
		].filter(Boolean) as ToolReferenceItem[])
		: []

	// Звук и «глазик» у превью-ролика карточки (владелец 12.09). Превью играет
	// фоном, поэтому по умолчанию тихо — но если человек уже просил звук в этой
	// сессии (лайтбокс, соседняя карточка), включаем сразу.
	const demoVideoRef = useRef<HTMLVideoElement>(null)
	const [demoSound, setDemoSound] = useState(() => soundPref() === true)
	const [demoLightbox, setDemoLightbox] = useState(false)

	// Узкий виджет (сайдбар, маленькое окно): ряд «пример · поля · результат» не помещается — фото-поля встают
	// колонкой (владелец 28.09: три поля в ряд выдавили колонку результата в ноль). Меряем сам ряд зон, а не окно:
	// сайдбар и окно меняют его ширину независимо. Первый замер ResizeObserver присылает сам, сразу после observe.
	const [zonesEl, setZonesEl] = useState<HTMLDivElement | null>(null)
	const [zonesNarrow, setZonesNarrow] = useState(false)
	useEffect(() => {
		if (!zonesEl || typeof ResizeObserver === 'undefined') return
		const ro = new ResizeObserver((entries) => {
			const width = entries[entries.length - 1]?.contentRect.width ?? zonesEl.getBoundingClientRect().width
			setZonesNarrow(width > 0 && width < ZONES_NARROW_PX)
		})
		ro.observe(zonesEl)
		return () => ro.disconnect()
	}, [zonesEl])
	// Один звучащий источник на страницу (utils/tileSound, владелец 16.09):
	// включили здесь — плитки лент и лайтбокс глохнут; включили там (или
	// открыли «глазик» со звуком) — глохнет превью.
	const [demoSoundToken] = useState(tileSoundToken)
	useEffect(() => onTileSoundClaimed(demoSoundToken, () => {
		setDemoSound(false)
		if (demoVideoRef.current) demoVideoRef.current.muted = true
	}), [demoSoundToken])
	const toggleDemoSound = () => {
		const v = demoVideoRef.current
		const next = !demoSound
		setDemoSound(next)
		if (next) claimTileSound(demoSoundToken)
		else releaseTileSound()
		if (!v) return
		v.muted = !next
		// Клик — жест пользователя, после него звук разрешён; play() на всякий
		// случай, если политика успела поставить ролик на паузу.
		void v.play().catch(() => {})
	}
	useEffect(() => {
		if (!autoAudioUrl || gen.referenceAudios.length) return
		gen.applyDefaultReferenceAudio(autoAudioUrl)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [card?.id, autoAudioUrl, gen.uploadsEpoch])

	// Промпт tool-карточки — «под капотом» (фишка инструментов), в UI не светится:
	// в фокус-режиме поле скрыто, а при ВЫХОДЕ (смена раздела/модели →
	// карточка отвязывается → компонент размонтируется, ИЛИ сворачивание)
	// чистим gen.prompt, иначе он утекает в классический бар и залипает там.
	// При повторном входе промпт восстановит applyCard/эффект выше из пина.
	const setPromptRef = useRef(gen.setPrompt)
	setPromptRef.current = gen.setPrompt
	useEffect(() => {
		return () => {
			// Свернули виджет — карточка на месте, рецепт не трогаем: после «развернуть» эффекты маунта его не
			// восстанавливают (у карточки без вариаций — вовсе), и генерация ушла бы без промпта (29.09). Открепили —
			// промпт гасит подписка на открепление в GenerateContext (во всех инстансах провайдера).
			if (getAttachedStyleCard()?.toolMode) return
			setPromptRef.current('')
		}
	}, [])

	return (
		<div className={`gtm${compact ? ' gtm--compact' : ''}`}>
			{/* × «Закрыть инструмент» (Artlist: Close app в углу зоны) — полный
			    выход С ОЧИСТКОЙ: тот же сброс, что у «Очистить» классического бара
			    (resetAll + detach) — в tool-режиме кнопка «Очистить» скрыта, ×
			    несёт её функцию. Обучающий hint-pill карточки тоже гасим. */}
			{card && (
			<button
				type="button"
				className="gtm__close"
				onClick={() => {
					// Пайплайн-job тоже в ноль: иначе он воскресает из
					// sessionStorage при повторном открытии карточки (тот же slug)
					// со СТАРЫМ config.user_photos — новые фото игнорировались.
					if (isPipeline) handlePipeReset()
					gen.resetAll()
					gen.setShowStyleHint(false)
					detachStyleCard()
					// Карточка снята — срезаем /tool/<slug> из адресной строки
					// (контекст галереи цел). Иначе URL держал старый слаг, и
					// повторный клик по ТОЙ ЖЕ карточке вёл на тот же адрес —
					// роутер молчал, карточка «не подставлялась», хотя любая
					// другая открывалась (шаги владельца). Вне tool-путей
					// регэксп не матчится — no-op.
					const stripped = pathWithoutTool(window.location.pathname)
					if (stripped) navigate(stripped, { replace: true })
					else {
						// Тот же срез для ссылки вида /generate?tool=<slug> (23.10, «Повторить движение»): иначе F5
						// после закрытия снова открывал снятый режим. Остальные параметры (?work=…) остаются.
						const qs = new URLSearchParams(window.location.search)
						if (qs.has('tool')) {
							qs.delete('tool')
							const rest = qs.toString()
							navigate({ pathname: window.location.pathname, search: rest ? `?${rest}` : '' }, { replace: true })
						}
					}
				}}
				disabled={gen.isLoading}
				aria-label="Закрыть инструмент"
				title={gen.isLoading ? 'Идёт генерация…' : 'Закрыть инструмент'}
			>
				<svg width="13" height="13" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true">
					<line x1="3" y1="3" x2="11" y2="11" />
					<line x1="11" y1="3" x2="3" y2="11" />
				</svg>
			</button>
			)}
			{/* «Очистить» режима «Повторить движение» (владелец 18.10): ролик, люди и аватары в ноль, результат тоже —
			    режим и нейросеть остаются (× рядом закрывает инструмент целиком). Значок — наш привычный ↺ из классической
			    строки; показываем только когда есть что чистить. */}
			{isMotion && !isGuest && (!!motion.clip || motion.people.length > 0 || motion.anyFilled || !!gen.result) && (
			<button
				type="button"
				className="gtm__clear"
				onClick={() => motion.clearAll()}
				aria-label="Очистить"
				title="Очистить"
			>
				<ClearIcon size={13} />
			</button>
			)}
			{/* Компактный вид: кнопка слева от ×, тот же ghost-кружок. Гостю не
			    показываем — его виджет под стеклом CTA. Иконка — как «развернуть»
			    у примера (владелец 29.09: прежние шевроны читались как «закрыть»):
			    стрелки внутрь — уменьшить, наружу — вернуть обычный размер. key —
			    смена иконки проигрывается поворотом (design_language: key-remount). */}
			{card && !isGuest && (
			<button
				type="button"
				className="gtm__compact"
				onClick={toggleCompact}
				aria-pressed={compact}
				aria-label={compact ? 'Обычный размер виджета' : 'Компактный вид виджета'}
				title={compact ? 'Обычный размер' : 'Компактный вид'}
			>
				<span key={compact ? 'expand' : 'shrink'} className="gtm__compact-icon">
					<Icon id={compact ? 'gen-expand' : 'gen-shrink'} size={13} />
				</span>
			</button>
			)}
			{card?.title && <div className="gtm__title">{card.title}</div>}

			{/* Ошибки загрузки/генерации: в классическом баре есть gm__error, а
			    тут фейлы были НЕМЫМИ (аплоад падал → тумба исчезала без
			    объяснений — «фото не выбирается»). Показываем + закрытие. */}
			{gen.error && (
				<div className="gtm__error" role="alert">
					<span className="gtm__error-text">{gen.error}</span>
					<button
						type="button"
						className="gtm__error-close"
						onClick={() => gen.setError(null)}
						aria-label="Скрыть ошибку"
					>
						×
					</button>
				</div>
			)}
			<div
				ref={setZonesEl}
				className={`gtm__zones${photoOnly ? ' gtm__zones--flow' : ''}${arWide ? ' gtm__zones--m-wide' : ''}${zonesNarrow ? ' gtm__zones--narrow' : ''}${isMotion ? ' gtm__zones--motion' : ''}`}
			>
				{/* Пример «до/после» — часть КАРТОЧКИ. У «голой» модели карточки нет,
				    и пустой демо-блок занимал бы треть ряда серым прямоугольником. */}
				{isMotion ? (
					/* «Повторить движение»: вместо демо карточки — ролик с отрезком (блок «Ролик» Видеостудии). */
					<MotionClipZone
						motion={motion}
						onUpload={makeShowPicker('videos', gen.videoInputRef, 'uploadedVideo')}
						disabled={gen.isLoading || motion.cutting}
					/>
				) : card && (
				<div className="gtm__demo">
					{isMsty ? (
						<div className="gtm__msty">
							<div
								className="gtm__msty-hero"
								style={
									activeChip?.afterUrl || card?.previewUrl
										? { backgroundImage: `url(${activeChip?.afterUrl || card?.previewUrl})` }
										: undefined
								}
							/>
							<div
								ref={mstyRowRef}
								className="gtm__msty-row"
								role="tablist"
								aria-label="Стили"
								onScroll={updateMstyFade}
								onMouseDown={startRowDrag}
							>
								{styleChips.map((c) => (
									<button
										key={c.key}
										type="button"
										role="tab"
										aria-selected={c.key === activeKey}
										className={`gtm__msty-swatch${c.key === activeKey ? ' is-on' : ''}`}
										onClick={() => variant.selectVariant(c.key)}
										disabled={gen.isLoading}
										title={c.label}
									>
										{c.afterUrl ? <img src={c.afterUrl} alt={c.label} loading="lazy" /> : c.label.slice(0, 2)}
									</button>
								))}
								{/* «Свой вариант» — последним: свотч-карандаш (или обложка,
								    если задана). Клик собирает промпт из текущего значения. */}
								{openChips.map((c) => (
									<button
										key={c.key}
										type="button"
										role="tab"
										aria-selected={c.key === activeKey}
										className={`gtm__msty-swatch gtm__msty-swatch--open${c.key === activeKey ? ' is-on' : ''}`}
										onClick={() => variant.selectCustomVariant(c.key, customValue)}
										disabled={gen.isLoading}
										title={c.label}
									>
										{c.afterUrl ? (
											<img src={c.afterUrl} alt={c.label} loading="lazy" />
										) : (
											<PencilIcon size={16} />
										)}
									</button>
								))}
							</div>
						</div>
					) : hasSlider ? (
						<BeforeAfterSlider before={beforeUrl!} after={afterUrl!} className="gtm__slider" />
					) : card?.videoUrl ? (
						/* Видео-карточки: превью — ролик (background-image с .mp4 не
						   работает → раньше блок был пустым). Поверх — две кнопки:
						   звук (ролики карточек чаще всего со звуковой дорожкой, и
						   услышать её было негде) и «глазик» на полный просмотр. */
						<div className={`gtm__demo-video-wrap${trendRow ? ' gtm__demo-video-wrap--trends' : ''}`}>
							<video
								ref={demoVideoRef}
								className="gtm__demo-video"
								src={card.videoUrl}
								autoPlay
								muted={!demoSound}
								loop
								playsInline
								preload="metadata"
								/* Стартовал со звуком по запомненному выбору — объявляем: плитка,
								   с которой пришли, глохнет. */
								onPlay={(e) => { if (!e.currentTarget.muted) announceTileSound(demoSoundToken) }}
							/>
							{/* Бар «звук + развернуть» — общий CardMediaBar, как на плитках витрин
			    (владелец 15.09: один стандарт контролов на карточках). */}
							<CardMediaBar
								className="gtm__demo-bar"
								expandLabel="Посмотреть крупно"
								onExpand={() => setDemoLightbox(true)}
								sound={{ on: demoSound, onToggle: toggleDemoSound }}
							/>
							<ToolReferenceThumbs items={refThumbs} />
							{trendRow}
						</div>
					) : (
						<div
							className="gtm__demo-img"
							style={card?.previewUrl ? { backgroundImage: `url(${card.previewUrl})` } : undefined}
						>
							{trendRow}
						</div>
					)}
				</div>
				)}

				{/* Загрузки инструмента — МЕЖДУ демо и результатом (колонка крупных
				    слотов, прежний вид tool-режима). Сами поля рендерит ШТАТНЫЙ
				    UploadsRow классического бара: вся матрица входов модели
				    (фото / видео движения / референсы фото-видео-аудио / Seedance-
				    режимы / два фото / конечный кадр) — единый источник правды,
				    ноль дрейфа с баром; здесь только скоуп-рестайл (_tool-mode).
				    При админ-локе видео-слот скрыт (генерация с нашим референсом). */}
				<div className="gtm__uploads">
					{isMotion ? (
						/* «Кто повторит»: лица аватаров, «Все» (пикер — тот же хост, что у слота героя тренда), своё фото. */
						<>
							<MotionWhoZone
								motion={motion}
								onSlotMenu={(el, i, who) => {
									motion.openSlot(i)
									// Шапка меню: кого заменяем (кадр человека из ролика и подпись) — плитка и кружок открывают одно меню.
									makeShowPicker('images', gen.fileInputRef, undefined, undefined, undefined, who ? { ...who, caption: 'Кто встанет на место' } : null)(el)
								}}
								disabled={gen.isLoading || motion.cutting}
							/>
							{!isGuest && <ScenePickerHost pipeOnly onPipePick={(id) => { void motion.applyWho(id) }} />}
						</>
					) : isPipeline ? (
						/* Пайплайн: слоты фото ОТ ЭТАПОВ (max_count/подпись из
						   input_schema шаблона). ТЕ ЖЕ UploadThumb, что в 1-шаговом
						   tool-режиме — единый вид слотов (крупный dropzone с
						   подписью, превью+Tooltip, статус, remove), без самопального
						   мульти-ряда. Каждый слот со своим input/ref. */
						<>
							{Array.from({ length: pipeMax }).map((_, i) => (
								<PipeUploadSlot
									key={i}
									file={pipePhotos[i] ?? null}
									characterMark={
										pipeChar && pipePhotos[i]?.url && pipeChar.urls.includes(pipePhotos[i].url!)
											? { avatar: pipeChar.avatar ?? null, letter: pipeChar.name.slice(0, 1).toUpperCase() }
											: undefined
									}
									isNext={i === pipePhotos.length}
									busy={pipe.busy || pipeProcessing}
									uploading={pipeUploading}
									label={pipe.photoField?.label || 'Ваше фото'}
									onAdd={handlePipePhotos}
									onRemove={() => removePipePhoto(i)}
								/>
							))}
							{/* «Персонаж» — та же капсула-трансформер, что в обычном ряду
							    слотов (решение владельца 2026-08-01): один элемент несёт и
							    вход, и статус выбранного, на всех ширинах. В пайпе UploadsRow
							    не смонтирован, поэтому капсулу рендерим здесь.
							    Хосты-порталы ниже: событие пикера ловит этот экземпляр,
							    onPick кладёт фото в слот тренда. */}
							{!isGuest && <CharacterRow part="head" />}
							{!isGuest && <ScenePickerHost pipeOnly onPipePick={(id) => { void applyCharacterToPipe(id) }} />}
						</>
					) : (
						<UploadsRow makeShowPicker={makeShowPicker} faceSwapLayout={photoOnly} hideImageSlot={imageLocked} hideVideoSlot={videoLocked} hideAudioSlot={audioLocked} slotSkeletons namedSlots={card?.photo_slots} activeSlotNums={activeSlotNums} hideCharacter={card?.hide_character} photoLimit={card?.user_photo_limit} photoHint={card?.user_photo_hint} hideEndImage={card?.hide_end_image} endImageHint={card?.end_image_hint} characterHint={card?.character_hint} />
					)}
				</div>

				{/* Стрелка «входы → результат» (владелец 2026-08-16 — всегда):
				    цепочка «фото → результат» читается одинаково у всех
				    инструментов; на узких сетках её прячет медиа-правило. */}
				<span className="gtm__flow-arrow" aria-hidden="true">→</span>

				{/* результат в выбранном формате */}
				<div className="gtm__resultcol" style={motionAspect != null ? ({ '--clip-aspect': arCss } as CSSProperties) : undefined}>
					<div className={`gtm__result${arWide ? ' gtm__result--wide' : ''}`} style={{ aspectRatio: arCss }}>
						{isPipeline ? (
							/* ── Пайплайн: этапы фото → видео, версии, выбор ── */
							pipeWorking ? (
								/* Пайплайн: то же точечное поле, но подпись фазы остаётся —
								   она несёт ФАКТ (какой шаг идёт, какой вариант фото), а не
								   декоративный статус: варианты платные, видео — отдельный
								   шаг. Подпись лежит поверх поля, у нижнего края. */
								<div className="gtm__result-loading">
									<DotFieldLoader />
									<span className="gtm__result-t gtm__result-t--over">
										{pipe.phase === 'video_processing'
											? 'создаём видео…'
											: pipe.phase === 'photo_processing'
												? `генерируем фото · вариант ${pipe.photoAttempts.length + 1}…`
												/* job ещё создаётся: фазы нет, но работа уже идёт */
												: pipePhotoDone ? 'создаём видео…' : 'запускаем генерацию…'}
									</span>
								</div>
							) : pipe.phase === 'failed' ? (
								/* Фейл ЛЮБОГО шага — ошибка и «Повторить» важнее превью
								   (фото-ветка ниже перекрывала бы их при фейле видео). */
								<div className="gtm__result-empty">
									<span className="gtm__result-t">
										{pipe.job?.error_message || pipe.error || 'Ошибка генерации'}
									</span>
									<button type="button" className="gtm__pipe-retry" onClick={pipe.retryFailed}>
										Повторить
									</button>
								</div>
							) : pipe.phase === 'video_ready' && pipe.videoUrl ? (
								<MediaPlayer
									variant="video"
									src={pipe.videoUrl}
									autoPlay
									startMuted
									loop
									clickToPlay={false}
									showFullscreen={false}
									className="gtm__result-video"
								/>
							) : pipePhotoDone && pipe.selectedPhotoUrl ? (
								/* Ховер — крупное превью (тот же Tooltip, что у миниатюр
								   вариантов), клик — модал: рассмотреть кадр до оплаты
								   видео (просьба владельца 2026-07-24). */
								<Tooltip imagePreview={pipe.selectedPhotoUrl}>
									<button
										type="button"
										className="gtm__result-open"
										onClick={() => setPipeResultOpen(true)}
										aria-label="Открыть фото крупно"
										title="Открыть фото крупно"
									>
										<img src={pipe.selectedPhotoUrl} className="gtm__result-img" alt="Вариант фото" />
									</button>
								</Tooltip>
							) : (
								<ResultEmpty text={pipe.error || 'Результат появится здесь'} />
							)
						) : genRunning ? (
							/* Точечное поле на всю рамку — «зерно будущего кадра»
							   (см. DotFieldLoader). Текстовой строки нет: анимация
							   сама говорит, что кадр делается. Исключение — факт:
							   провайдер не пропустил звук и сервер пересоздаёт ролик
							   (28.09) — это дольше обычного, и клиент должен знать,
							   что ждать стоит и платить второй раз не придётся.
							   globalGen — чтобы после навигации (локальный gen сброшен,
							   но генерация идёт глобально) процесс был виден. */
							<div className="gtm__result-loading">
								<DotFieldLoader />
								{retry?.retrying && (
									<span className="gtm__result-retry" role="status">
										<span className="gtm__result-retry-t">
											<span className="gtm__pipe-spin" aria-hidden="true" />
											{RETRY_TITLE}
										</span>
										<span className="gtm__result-retry-n">{RETRY_NOTE}</span>
									</span>
								)}
							</div>
						) : gen.result ? (
							gen.isVideo ? (
								/* Видео-модели: результат — ролик, не картинка (в <img>
								   video-URL рендерился битой иконкой). Тот же MediaPlayer,
								   что в большой result-зоне (/generate). На хосте с модалом
								   результата ролик кликабелен, как и картинка: на мобиле
								   рамка — компактная лента-превью, полный просмотр и
								   скачивание — в модале (контролов внутри нет:
								   clickToPlay/fullscreen выключены — клик не конфликтует). */
								onOpenResult ? (
									<button
										type="button"
										className="gtm__result-open"
										onClick={() => onOpenResult(shownResult ?? undefined)}
										aria-label="Открыть результат"
										title="Открыть результат"
									>
										<MediaPlayer
											key={shownResult}
											variant="video"
											src={shownResult as string}
											autoPlay
											startMuted={!track.heard}
											loop
											clickToPlay={false}
											showFullscreen={false}
											className="gtm__result-video"
										/>
									</button>
								) : (
									<MediaPlayer
										key={shownResult}
										variant="video"
										src={shownResult as string}
										autoPlay
										startMuted={!track.heard}
										loop
										clickToPlay={false}
										showFullscreen={false}
										className="gtm__result-video"
									/>
								)
							) : onOpenResult ? (
								/* Клик по готовой картинке — модал результата хоста
								   (скачать/воссоздать/создать видео; промпт и модель
								   скрыты — см. promptLocked в сборке card хоста). */
								<button
									type="button"
									className="gtm__result-open"
									onClick={() => onOpenResult()}
									aria-label="Открыть результат"
									title="Открыть результат"
								>
									<img src={gen.result} className="gtm__result-img" alt="Результат" />
								</button>
							) : (
								<img src={gen.result} className="gtm__result-img" alt="Результат" />
							)
						) : (
							<ResultEmpty text="Результат появится здесь" />
						)}
						{/* Пайплайн: лента версий фото поверх низа рамки — «ещё» копит
						    варианты (каждый платный), клик выбирает, видео — из выбранной.
						    Во время пересоздания лента остаётся (видно, куда встанет
						    новая версия — pending-плитка), действия заморожены. */}
						{isPipeline && pipe.photoAttempts.length > 0 && (
							<div className="gtm__pipe-vers">
								{pipe.photoAttempts.map((a, i) => (
									/* Ховер — крупное превью варианта (тот же Tooltip, что у
									   загруженных фото): выбирать глазами, не по миниатюре. */
									<Tooltip key={a.result_url} imagePreview={a.result_url}>
										<button
											type="button"
											className={`gtm__pipe-ver${a.result_url === pipe.selectedPhotoUrl ? ' gtm__pipe-ver--on' : ''}`}
											onClick={() => pipe.choosePhoto(a.result_url)}
											disabled={pipeProcessing || pipe.busy}
											title={`Вариант ${i + 1} — выбрать для видео`}
										>
											<img src={a.result_url} alt={`Вариант ${i + 1}`} />
										</button>
									</Tooltip>
								))}
								{pipe.phase === 'photo_processing' ? (
									<span className="gtm__pipe-ver gtm__pipe-ver--pending" aria-label="Генерируем вариант">
										<span className="gtm__pipe-spin" />
									</span>
								) : (
									<button
										type="button"
										className="gtm__pipe-more"
										onClick={() => pipe.createPhoto(pipePhotoUrls, pipeCharActive)}
										disabled={pipe.busy || pipeProcessing}
										title={`Сгенерировать ещё вариант фото · ${pipe.photoCost} кр`}
										aria-label={`Сгенерировать ещё вариант фото · ${pipe.photoCost} кредитов`}
									>
										<Icon id="gen-reuse" size={15} />
										<span>ещё</span>
									</button>
								)}
							</div>
						)}
						{/* Пайплайн: открыть результат крупно — и на этапе ФОТО (кадр
						    видно до оплаты видео), и на готовом видео. Кнопка одна,
						    меняется только источник (см. модал внизу). */}
						{isPipeline && ((pipe.phase === 'video_ready' && pipe.videoUrl) || (pipePhotoDone && pipe.selectedPhotoUrl)) && (
							<button
								type="button"
								className="gtm__pipe-expand"
								onClick={() => setPipeResultOpen(true)}
								aria-label="Открыть результат"
								title="Открыть результат"
							>
								<svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden="true">
									<path d="M9 3H3v6M15 21h6v-6M21 9V3h-6M3 15v6h6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
								</svg>
							</button>
						)}
						{/* Очистка поля результата: убирает картинку/ролик из рамки
						    (и persisted-снапшот категории), форму не трогает —
						    можно сразу генерить заново. */}
						{gen.result && !gen.isLoading && globalGen.status !== 'running' && (
							<button
								type="button"
								className="gtm__result-clear"
								onClick={() => gen.resetResult()}
								aria-label="Очистить результат"
								title="Очистить результат"
							>
								<svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
									<path d="M2 2l8 8M10 2l-8 8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
								</svg>
							</button>
						)}
						{/* Звук ролика с треком (28.09): «Наложить трек» — бесплатно, модель не
						    запускается; после неё — «Оригинал | Трек» (оригинал — звук модели,
						    трек — наш слой). Тот же бар, что у ленты версий пайплайна. */}
						{!isPipeline && gen.isVideo && gen.result && !genRunning && (track.version || track.available) && (
							<div className="gtm__pipe-vers gtm__track-bar">
								{track.version ? (
									<div className="gtm__track-seg" role="group" aria-label="Звук ролика">
										<button
											type="button"
											className={track.showTrack ? undefined : 'is-on'}
											aria-pressed={!track.showTrack}
											onClick={() => track.select(false)}
											title="Оригинал — звук модели"
										>
											Оригинал
										</button>
										<button
											type="button"
											className={track.showTrack ? 'is-on' : undefined}
											aria-pressed={track.showTrack}
											onClick={() => track.select(true)}
											title="Трек тренда поверх ролика"
										>
											<Icon id="gen-wave" size={13} />
											Трек
										</button>
									</div>
								) : (
									<button
										type="button"
										className="gtm__track-btn"
										onClick={() => void track.overlay()}
										disabled={track.busy}
										title="Наложить трек на ролик — бесплатно, оригинал останется"
									>
										{track.busy ? <span className="gtm__pipe-spin" aria-hidden="true" /> : <Icon id="gen-wave" size={15} />}
										{track.busy ? 'Накладываем…' : 'Наложить трек'}
									</button>
								)}
							</div>
						)}
						{/* Пайплайн: «начать заново» — полный сброс прогона (версии,
						    видео, фото юзера); тот же крестик, что у одношагового.
						    Готовые генерации остаются в «Моих генерациях». И пока шаг
						    генерируется — иначе выхода не было до конца генерации (без
						    часов — до трёх суток, ревью 27.09): идущая досчитается сама. */}
						{isPipeline && pipe.job && !pipe.busy && (
							<button
								type="button"
								className="gtm__result-clear"
								onClick={handlePipeReset}
								aria-label="Начать заново"
								title={pipeProcessing
									? 'Начать заново — идущая генерация досчитается и появится в «Моих генерациях»'
									: 'Начать заново — очистит версии, видео и загруженные фото'}
							>
								<svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
									<path d="M2 2l8 8M10 2l-8 8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
								</svg>
							</button>
						)}
					</div>
				</div>
			</div>

			{/* нижняя панель: стиль-селект + настройки модели + Создать */}
			{/* «Повторить движение»: свой класс бара — сводка настроек ужимается, «Создать» не уезжает на вторую строку. */}
			<div className={`gtm__bar${isMotion ? ' gtm__bar--motion' : ''}`}>
				{/* Пайплайн: индикатор этапов «① Фото › ② Видео». */}
				{isPipeline && (
					<span className="gtm__pipe-steps" aria-label="Этапы">
						<span className={`gtm__pipe-step${pipePhotoDone ? ' gtm__pipe-step--done' : ' gtm__pipe-step--on'}`}>
							{pipePhotoDone ? '✓' : '1'} Фото
						</span>
						<span className="gtm__pipe-sep">›</span>
						<span className={`gtm__pipe-step${pipe.phase === 'video_ready' ? ' gtm__pipe-step--done' : pipePhotoDone ? ' gtm__pipe-step--on' : ''}`}>
							{pipe.phase === 'video_ready' ? '✓' : '2'} Видео
						</span>
					</span>
				)}
				{chips.length > 0 && (
					<div className={`gtm__style${styleOpen ? ' is-open' : ''}`}>
						<button className="gtm__style-btn" type="button" onClick={() => setStyleOpen((v) => !v)} disabled={gen.isLoading}>
							<span className="gtm__style-k">Стиль:</span> {activeChip?.label}
							{/* Мини-превью — ТОЛЬКО у мультистиля (владелец 2026-08-17):
							    у обычных вариаций after_url — кадры слайдера, в селекте
							    они лишние. */}
							{isMsty && activeChip?.afterUrl && (
								<img className="gtm__style-thumb" src={activeChip.afterUrl} alt="" />
							)}
							<span className="gtm__style-ch">▾</span>
						</button>
						{styleOpen && (
							<>
								<div className="gtm__style-backdrop" onClick={() => setStyleOpen(false)} />
								<div className="gtm__style-menu">
									{chips.map((c) => (
										<button
											key={c.key}
											className={`gtm__style-opt${
												c.key === activeKey ? ' is-active' : ''
											}`}
											type="button"
											onClick={() => {
												if (c.isOpen) {
													// Open-вариация: промпт соберётся из текущего
													// customValue (пустое → чистая база); инпут
													// раскроется рядом с селектом.
													variant.selectCustomVariant(c.key, customValue)
												} else {
													variant.selectVariant(c.key)
												}
												setStyleOpen(false)
											}}
										>
											{isMsty && c.afterUrl && <img className="gtm__style-opt-thumb" src={c.afterUrl} alt="" loading="lazy" />}
											{c.label}
										</button>
									))}
								</div>
							</>
						)}
					</div>
				)}

				{/* «Свой стиль»: инпут значения активной open-вариации — прямо в
				    баре рядом с селектом. Ввод живьём собирает промпт под капотом
				    ({value} в шаблоне патча → введённое); пусто → чистая база.
				    Гость (promptLocked): шаблон обрезан бэком — инпут неактивен. */}
				{isCustomActive && activeChip?.valueType === 'number' ? (
					/* Числовое «своё значение» (владелец 2026-08-14): степпер с
					   жёстким клампом по границам из конструктора. Значение уходит
					   тем же каналом applyCustom (строкой) — сборка промпта общая. */
					<NumberStepper
						value={customValue.trim() ? parseInt(customValue, 10) : null}
						onChange={(v) => applyCustom(v == null ? '' : String(v))}
						min={activeChip?.minValue ?? undefined}
						max={activeChip?.maxValue ?? undefined}
						disabled={gen.isLoading || card?.promptLocked}
						ariaLabel={activeChip?.placeholder || 'Число'}
					/>
				) : isCustomActive ? (
					<input
						className="gtm__style-input"
						type="text"
						maxLength={80}
						placeholder={activeChip?.placeholder || 'Впишите свой вариант'}
						value={customValue}
						onChange={(e) => applyCustom(e.target.value)}
						disabled={gen.isLoading || card?.promptLocked}
					/>
				) : null}

				{/* Мультистиль: карточное поле юзера ЖИВЁТ РЯДОМ с выбором стиля
				    (владелец 2026-08-14) — значение встаёт на {value} в патче
				    выбранного стиля. Число — степпером с клампом. Показ ЗАВИСИТ
				    от стиля (владелец 2026-08-16): стиль без {value} в промпте
				    поле не показывает. До restored-эффекта контекст пуст —
				    смотрим патч пин-чипа и базу из пина. */}
				{/* При активном «своём варианте» общее поле прячем: {value} в его
				    шаблоне занят вводом самого варианта — двойное поле путало бы. */}
				{activeUserInputGtm && activeUsesValueGtm && !isCustomActive && (
					<span className="gtm__uval">
						<span className="gtm__uval-k">{activeUserInputGtm.label || 'Значение'}</span>
						{activeUserInputGtm.type === 'number' ? (
							<NumberStepper
								value={variant.cardUserValue.trim() ? parseInt(variant.cardUserValue, 10) : null}
								onChange={(v) => variant.setUserValue(v == null ? '' : String(v))}
								min={activeUserInputGtm.min ?? undefined}
								max={activeUserInputGtm.max ?? undefined}
								disabled={gen.isLoading}
								ariaLabel={activeUserInputGtm.label || 'Число'}
							/>
						) : (
							<input
								className="gtm__style-input"
								type="text"
								maxLength={80}
								placeholder={activeUserInputGtm.placeholder || 'Введите значение'}
								value={variant.cardUserValue}
								onChange={(e) => variant.setUserValue(e.target.value)}
								disabled={gen.isLoading}
							/>
						)}
					</span>
				)}

				{/* Один поповер инструментов (Artlist) — как в классическом баре.
				    В пайплайне скрыт: формат и параметры диктует шаблон админки. */}
				{/* «Повторить движение»: нейросеть — штатной пилюлей на месте классического бара (список — нейросети режима). */}
				{isMotion && <WidgetModelPicker category="video" />}
				{!isPipeline && <SettingsToolsPill />}

				<span className="gtm__spacer" />

				{/* Навигация по разделам — та же, что в классическом баре (общий
				    WidgetSectionNav). В tool-режиме её не было вовсе: юзер,
				    открывший тренд с мобилы, мог уйти в другой раздел только
				    через бургер. Показывается только на мобиле — на десктопе
				    разделы всегда видны в сайдбаре (CSS в _tool-mode.scss). */}
				<span className="gtm__section-nav">
					<WidgetSectionNav />
				</span>

				{/* ЕДИНАЯ кнопка «Создать» (общая с классическим баром) — тот же
				    gen.handleGenerate, цена — параметром. Инструмент с фото-входом
				    БЕЗ единого фото не запускаем: он преобразует изображение юзера,
				    генерация впустую списала бы кредиты. */}
				{isPipeline ? (
					<CreateButton
						onClick={() =>
							pipePhotoDone ? pipe.createVideo() : pipe.createPhoto(pipePhotoUrls, pipeCharActive)
						}
						disabled={
							pipe.busy || pipeProcessing || pipePending || (!pipePhotoDone && pipePhotoUrls.length === 0) || toolShort
						}
						loading={pipe.busy || pipeProcessing}
						label={pipePhotoDone ? 'Создать видео' : 'Создать фото'}
						cost={pipePhotoDone ? pipe.videoCost : pipe.photoCost}
						title={
							toolShort && toolCost != null ? guard.titleFor(toolCost)
								: !pipePhotoDone && pipePhotoUrls.length === 0
								? 'Добавьте фото — тренд собирается из вашего изображения'
								: undefined
						}
					/>
				) : (
					<>
					{gen.isLoading && <DetachButton onClick={gen.detachGeneration} />}
					<CreateButton
						onClick={isMotion ? () => { void motion.launch() } : gen.handleGenerate}
						disabled={gen.isLoading || gen.hasPendingUploads || (isMotion ? motion.missing || motion.busy || motion.cutting : noPhotoYet) || toolShort || toolCostHold}
						loading={gen.isLoading || motion.cutting}
						cost={toolCostHold ? null : gen.currentCost}
						title={toolCostHold ? (gen.costIssue ?? 'Считаем цену с видео на входе…') : toolShort && toolCost != null ? guard.titleFor(toolCost) : customValueMissing ? 'Впишите свой вариант — он входит в промпт' : userValueMissing
							? `Заполните поле «${activeUserInputGtm?.label || 'значение'}» — оно входит в промпт этого стиля`
							: isMotion && motion.missing ? (!motion.clip?.url ? 'Добавьте ролик и того, кто его повторит' : motion.peopleMode ? 'Положите аватара хотя бы на одного человека из ролика' : 'Заполните все слоты «Кто повторит»')
							: noPhotoYet ? 'Добавьте фото — инструмент работает с вашим изображением' : undefined}
					/>
					</>
				)}
			{toolShort && <CreditsShortNote price={toolCost} />}
			{!isPipeline && gen.costIssue && <div className="gm__credits-short" role="status">{gen.costIssue}</div>}
			</div>

			{/* Гость: матовое стекло поверх инструмента + CTA входа — виджет
			    просвечивает (демо продолжает играть под стеклом), интеракция
			    заблокирована. × «Закрыть» остаётся НАД стеклом (z-index).
			    Кнопки — те же, что у гейта промпта (.gm__prompt-locked__btn). */}
			{isGuest && (
				<div className="gtm__guest" role="dialog" aria-label="Нужен вход">
					{/* Капсула отделяет CTA от просвечивающего интерфейса: без неё
					    на длинном мобильном виджете замок «садился» в плитку слота. */}
					<div className="gtm__guest-card">
						<Icon id="gen-lock" size={22} className="gtm__guest-icon" />
						<div className="gtm__guest-t">Войдите, чтобы создать</div>
						<div className="gtm__guest-s">
							{isPipeline
								? 'Тренд соберётся из вашего фото за пару минут'
								: 'Быстрый вход — и всё готово к генерации'}
						</div>
						<div className="gtm__guest-btns">
							<a {...authLink('/register')} className="gm__prompt-locked__btn">Регистрация</a>
							<a {...authLink('/login')} className="gm__prompt-locked__btn gm__prompt-locked__btn--ghost">Войти</a>
						</div>
					</div>
				</div>
			)}

			{/* Пикер источника файла + галерея «Мои генерации» (общий хук) */}
			{pickerPortal}
			{galleryModal}

			{/* Модал готового видео пайплайна: промпт/модель скрыты (promptLocked). */}
			{pipeResultOpen && (pipe.videoUrl || pipe.selectedPhotoUrl) && (
				<CardModal
					card={{
						id: pipe.job?.id ?? 'pipeline',
						type: 'generation',
						previewUrl: pipe.videoUrl || pipe.selectedPhotoUrl || '',
						prompt: '',
						promptLocked: true,
						modelId: '',
						modelName: '',
						isVideo: !!pipe.videoUrl,
						kind: pipe.videoUrl ? 'video' : 'image',
						createdAt: Date.now(),
						settings: {},
					}}
					onClose={() => setPipeResultOpen(false)}
				/>
			)}
			{demoLightbox && card?.videoUrl && (
				<CardImageLightbox
					images={[{ url: card.previewUrl || '', video: card.videoUrl, label: card.title }]}
					onClose={() => setDemoLightbox(false)}
				/>
			)}
		</div>
	)
}
